import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { MessageSquare, ExternalLink, CheckCircle, Copy, Twitter, ArrowRight, ArrowLeft } from 'lucide-react';

function ContactPage({ Header, Footer }) {
  const [copied, setCopied] = useState(false);

  const handleCopyEmail = () => {
    navigator.clipboard.writeText('service@8004agents.ai');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleEmailClick = () => {
    window.location.href = 'mailto:service@8004agents.ai';
  };

  return (
    <div className="min-h-screen bg-[#f8f9fa]">
      <Helmet>
        <title>Contact Us | ERC-8004 Explorer</title>
        <meta name="description" content="Get in touch with the ERC-8004 Explorer team. We're here to help with questions about AI agents, the ERC-8004 standard, and our platform." />
        <link rel="canonical" href="https://8004agents.ai/contact" />
      </Helmet>

      <Header
        stats={null}
        chainFilter="all"
        onChainFilterChange={() => {}}
        chains={[]}
        refreshing={false}
        onRefresh={() => {}}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Explorer
        </Link>
      </div>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-16">
        <div className="text-center mb-10 sm:mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl mb-4 sm:mb-6">
            <MessageSquare className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
          </div>
          <h1 className="text-2xl sm:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">Get in Touch</h1>
          <p className="text-base sm:text-lg text-gray-600 max-w-xl mx-auto">
            Have questions about ERC-8004, need help with your agent, or want to collaborate? We'd love to hear from you.
          </p>
        </div>

        <div className="grid gap-4 sm:gap-6 mb-10 sm:mb-12">
          <div className="bg-white rounded-xl sm:rounded-2xl border border-gray-200 p-5 sm:p-8 hover:border-blue-300 hover:shadow-lg transition-all">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="p-2.5 sm:p-3 bg-blue-50 rounded-xl">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900">Email Us</h3>
                  <p className="text-sm sm:text-base text-gray-600">Questions, feedback, or partnerships</p>
                </div>
              </div>
              <div className="flex items-center gap-2 sm:gap-3">
                <button
                  onClick={handleEmailClick}
                  className="flex-1 sm:flex-none px-4 sm:px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium text-sm transition text-center"
                >
                  Send Email
                </button>
                <button
                  onClick={handleCopyEmail}
                  className="p-2.5 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg transition"
                  title="Copy email address"
                >
                  {copied ? (
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  ) : (
                    <Copy className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl sm:rounded-2xl border border-gray-200 p-5 sm:p-8 hover:border-blue-300 hover:shadow-lg transition-all">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="p-2.5 sm:p-3 bg-sky-50 rounded-xl">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-sky-500" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.446 1.394c-.14.18-.357.295-.6.295l.213-3.054 5.56-5.022c.24-.213-.054-.334-.373-.121l-6.869 4.326-2.96-.924c-.64-.203-.658-.64.135-.954l11.566-4.458c.538-.196 1.006.128.828.94z"/>
                  </svg>
                </div>
                <div>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900">Telegram Community</h3>
                  <p className="text-sm sm:text-base text-gray-600">Join the ERC-8004 community chat</p>
                </div>
              </div>
              
                <a href="https://t.me/ERC8004"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 px-4 sm:px-5 py-2.5 bg-sky-500 hover:bg-sky-600 text-white rounded-lg font-medium text-sm transition"
              >
                Ask In Telegram
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>

        </div>

        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl sm:rounded-2xl border border-blue-200 p-5 sm:p-8 text-center">
          <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">Common Questions</h3>
          <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
            Before reaching out, you might find your answer in our resources.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
            
              <a href="https://eips.ethereum.org/EIPS/eip-8004"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 bg-white hover:bg-gray-50 text-gray-700 rounded-lg font-medium text-sm border border-gray-200 transition w-full sm:w-auto justify-center"
            >
              EIP-8004 Spec
              <ExternalLink className="w-4 h-4" />
            </a>
            
              <a href="https://github.com/erc-8004"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 bg-white hover:bg-gray-50 text-gray-700 rounded-lg font-medium text-sm border border-gray-200 transition w-full sm:w-auto justify-center"
            >
              Documentation
              <ExternalLink className="w-4 h-4" />
            </a>
            <Link
              to="/news"
              className="flex items-center gap-2 px-4 py-2 bg-white hover:bg-gray-50 text-gray-700 rounded-lg font-medium text-sm border border-gray-200 transition w-full sm:w-auto justify-center"
            >
              Latest News
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </main>

      <Footer stats={null} agents={[]} />
    </div>
  );
}

export default ContactPage;